import { useCallback, useMemo, useRef, useState } from "react";

import { Box, Table as MuiTable, TableBody, TableCell, TableContainer, TableRow as MuiTableRow } from "@mui/material";
import { genericsMemo, useLocalStorage } from "@bridgesplit/react";

import { SPACING } from "../../theme";
import { Column } from "../containers";
import { TABLE_BUFFER_HEIGHT, TABLE_ROW_HEIGHT, TableRow, TableColumn, TableKey } from "./types";
import { EmptyPlaceholder, Pagination } from "../misc";
import { Body } from "./Body";
import { BodySkeleton } from "./BodySkeleton";
import { HeaderRow } from "./HeaderRow";
import { Toolbar } from "./Toolbar";
import { GroupedBody } from "./grouped/GroupedBody";
import { TableProps } from "./tableProps";
import { useExpansion } from "./useExpansion";
import { useSelection } from "./useSelection";
import { useTable } from "./useTable";

const TABLE_BODY_SX = {
    minWidth: "fit-content",
    overflow: "auto",
    td: { border: "none" },
    th: { border: "none", pt: 0.5, pb: 1.5 }
} as const;

const OUTER_SX = (spacing: number) => ({
    width: `calc(100% + ${spacing * 2}px)`,
    mx: -1
});

/**
 * Unified table component.
 *
 * Three render modes, chosen by `grouping` + internal `flatView` state:
 *   1. No `grouping`            → flat table of `TRow`s (classic).
 *   2. `grouping` + grouped     → summary rows with expandable detail children.
 *   3. `grouping` + flat view   → children unrolled into the body, no parents shown.
 *
 * Modes 2↔3 are toggled via the toolbar's view switch (enable with
 * `showViewToggle`). Table owns the toggle state by default; consumers can
 * control it via `flatView` + `onFlatViewChange`.
 *
 * Pagination, sort, and filters always operate on the parent (`TRow`) rows —
 * flat view is purely a render concern, the underlying page of data doesn't
 * change.
 */
function TableImpl<TRow, TDetail = TRow>(props: TableProps<TRow, TDetail>) {
    const {
        rows,
        columns,
        paginationProps,
        grouping,
        selection,
        filters,
        showResetFilters,
        onResetFilters,
        onRowClick,
        onRowNavigatePath,
        isRowDisabled,
        toolbarActions,
        emptyPlaceholder = "No data",
        loading,
        fixTableHeight,
        skeletonRowCount,
        showViewToggle,
        flatView: controlledFlatView,
        defaultFlatView,
        onFlatViewChange,
        viewToggleStorageKey
    } = props;

    // ── Flat-view state (controlled + uncontrolled) ──────────────────────
    // When `viewToggleStorageKey` is set, the uncontrolled state persists across
    // renders/sessions. Both hooks run unconditionally; we pick which to use.
    const localState = useState(defaultFlatView ?? false);
    const persistedState = useLocalStorage(viewToggleStorageKey ?? "", defaultFlatView ?? false);
    const [internalFlatView, setInternalFlatView] = viewToggleStorageKey ? persistedState : localState;
    const flatView = controlledFlatView !== undefined ? controlledFlatView : internalFlatView;
    // `useLocalStorage`'s setter is re-created each render; funnel it through a ref so `setFlatView`
    // stays referentially stable and doesn't bust the toolbar's memoization.
    const setInternalFlatViewRef = useRef(setInternalFlatView);
    setInternalFlatViewRef.current = setInternalFlatView;
    const setFlatView = useCallback(
        (next: boolean) => {
            if (controlledFlatView === undefined) setInternalFlatViewRef.current(next);
            onFlatViewChange?.(next);
        },
        [controlledFlatView, onFlatViewChange]
    );

    // Mode discriminators. `isGroupedRender` = parent-rows-with-expansion;
    // `isFlatFromGroup` = same data, unrolled into the body.
    const isGroupedRender = !!grouping && !flatView;
    const isFlatFromGroup = !!grouping && flatView;

    // ── Top-level state hooks ────────────────────────────────────────────
    // Pagination/sort/filter always work on parent (TRow) rows.
    const table = useTable<TRow>({ rows, columns, filters, paginationProps });
    const expansion = useExpansion();

    // Derived flat rows for `isFlatFromGroup`. Memoized so identity is stable
    // when the underlying data hasn't shifted.
    const flatVisibleRows = useMemo<TableRow<TDetail>[] | undefined>(() => {
        if (!isFlatFromGroup || !grouping || !table.visibleRows) return undefined;
        const out: TableRow<TDetail>[] = [];
        for (const parent of table.visibleRows) {
            const items = grouping.getItems(parent.key) ?? [];
            for (const item of items) out.push({ data: item, key: grouping.getDetailKey(item) });
        }
        return out;
    }, [isFlatFromGroup, grouping, table.visibleRows]);

    // ── Selection wiring ─────────────────────────────────────────────────
    // Grouped: parent rows; checkbox toggles all that parent's detail items.
    // Flat-from-group: flat detail rows; each checkbox toggles one item.
    // Classic flat (no grouping): rows are TRow; checkbox toggles one row.
    const getItemsForRow = useCallback(
        (row: TableRow<TRow>): (TRow | TDetail)[] => {
            if (grouping) return (grouping.getItems(row.key) ?? []) as (TRow | TDetail)[];
            return [row.data];
        },
        [grouping]
    );
    // Funnel the key callbacks through refs so `getKey` stays referentially stable and doesn't bust
    // `useSelection`'s downstream memoization. Consumers pass these as inline arrows (e.g.
    // `getDetailKey: (item) => item.id`), so extracting the bare callbacks isn't enough — their
    // references still change every render.
    const getDetailKeyRef = useRef(grouping?.getDetailKey);
    getDetailKeyRef.current = grouping?.getDetailKey;
    const getRowKeyRef = useRef(selection?.getRowKey);
    getRowKeyRef.current = selection?.getRowKey;
    const getKey = useCallback((item: TRow | TDetail): TableKey => {
        if (getDetailKeyRef.current) return getDetailKeyRef.current(item as TDetail);
        if (getRowKeyRef.current) return getRowKeyRef.current(item as TRow);
        return "__unset__";
    }, []);

    // Cast at the boundary: `useSelection` is typed against TRow rows, but in
    // flat-from-group we feed it TDetail rows + a 1-to-1 getItemsForRow. The
    // hook only uses the values dispatched through these callbacks, so the
    // cast is sound at runtime.
    const selectionVisibleRows = isFlatFromGroup
        ? (flatVisibleRows as unknown as TableRow<TRow>[] | undefined)
        : table.visibleRows;
    const selectionGetItemsForRow = isFlatFromGroup
        ? (row: TableRow<TRow>): (TRow | TDetail)[] => [row.data as unknown as TDetail]
        : getItemsForRow;

    const sel = useSelection<TRow, TDetail>({
        // `expandOnGroupSelect` only fires in the parent-rows render path.
        isGrouped: isGroupedRender,
        selection,
        visibleRows: selectionVisibleRows,
        getItemsForRow: selectionGetItemsForRow,
        getKey,
        setGroupsExpanded: expansion.setGroupsExpanded
    });

    // ── Derived UI state ─────────────────────────────────────────────────
    const rowsPerPage = table.pageSize;

    // In flat-from-group, the header shows the detail columns. The sort
    // affordance only renders when a column has `sortBy` and the controller
    // exposes `setOrderBy` (see HeaderRow), so columns without working sorting
    // show no button rather than an inert one.
    const headerColumns = (isFlatFromGroup ? grouping.columns : columns) as TableColumn<TRow>[];

    const headerNode = (
        <HeaderRow<TRow>
            columns={headerColumns}
            isGrouped={isGroupedRender}
            orderBy={table.sort.orderBy}
            order={table.sort.order}
            setOrder={table.sort.setOrder}
            setOrderBy={table.sort.setOrderBy}
            resetPage={table.page.resetPage}
            headerCheckbox={sel.headerCheckbox}
        />
    );

    // ── Loading ──────────────────────────────────────────────────────────
    if (rows === undefined || loading) {
        const totalColSpan = headerColumns.length + (selection ? 1 : 0) + (isGroupedRender ? 1 : 0);
        return (
            <Column>
                <Column sx={OUTER_SX(SPACING)}>
                    <MuiTable sx={TABLE_BODY_SX}>
                        {headerNode}
                        <TableBody>
                            <MuiTableRow>
                                <TableCell colSpan={totalColSpan} sx={{ p: 0, border: "none" }}>
                                    <BodySkeleton rowsPerPage={skeletonRowCount ?? rowsPerPage} />
                                </TableCell>
                            </MuiTableRow>
                        </TableBody>
                    </MuiTable>
                </Column>
            </Column>
        );
    }

    // ── Empty ────────────────────────────────────────────────────────────
    if (table.totalCount === 0) {
        const placeholder =
            typeof emptyPlaceholder === "string" ? (
                <EmptyPlaceholder header={emptyPlaceholder} />
            ) : (
                <EmptyPlaceholder {...emptyPlaceholder} />
            );
        const minHeight = fixTableHeight ? `${TABLE_ROW_HEIGHT * rowsPerPage + TABLE_BUFFER_HEIGHT}px` : undefined;
        return (
            <Column>
                <Toolbar<TRow>
                    filters={filters}
                    activeFilterCount={table.activeFilterCount}
                    showResetFilters={showResetFilters}
                    onResetFilters={onResetFilters}
                    toolbarActions={toolbarActions}
                    showViewToggle={showViewToggle && !!grouping}
                    flatView={flatView}
                    onFlatViewChange={setFlatView}
                />
                <Box
                    sx={{
                        minHeight,
                        justifyContent: "center",
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    {placeholder}
                </Box>
            </Column>
        );
    }

    // ── Body ─────────────────────────────────────────────────────────────
    let bodyNode: JSX.Element;
    if (isGroupedRender) {
        bodyNode = (
            <GroupedBody<TRow, TDetail>
                visibleRows={table.visibleRows!}
                columns={columns}
                grouping={grouping!}
                rowCheckboxByKey={sel.rowCheckboxByKey}
                selectionContextValue={sel.selectionContextValue}
                expandedGroups={expansion.expandedGroups}
                setGroupsExpanded={expansion.setGroupsExpanded}
            />
        );
    } else if (isFlatFromGroup) {
        // Render flat detail rows. Body is generic over the row type — cast at the
        // boundary so its internals stay typed.
        const flatColumns = grouping!.columns as unknown as TableColumn<TRow>[];
        const flatRows = flatVisibleRows as unknown as TableRow<TRow>[];
        const detailNavigate = grouping!.onDetailRowNavigatePath;
        bodyNode = (
            <Body<TRow>
                visibleRows={flatRows}
                columns={flatColumns}
                rowCheckboxByKey={sel.rowCheckboxByKey}
                hasSelection={!!selection}
                onRowClick={undefined}
                onRowNavigatePath={detailNavigate ? (row) => detailNavigate(row.data as unknown as TDetail) : undefined}
                isRowDisabled={undefined}
            />
        );
    } else {
        bodyNode = (
            <Body<TRow>
                visibleRows={table.visibleRows!}
                columns={columns}
                rowCheckboxByKey={sel.rowCheckboxByKey}
                hasSelection={!!selection}
                onRowClick={onRowClick}
                onRowNavigatePath={onRowNavigatePath}
                isRowDisabled={isRowDisabled}
            />
        );
    }

    return (
        <Column>
            <Toolbar<TRow>
                filters={filters}
                activeFilterCount={table.activeFilterCount}
                showResetFilters={showResetFilters}
                onResetFilters={onResetFilters}
                toolbarActions={toolbarActions}
                showViewToggle={showViewToggle && !!grouping}
                flatView={flatView}
                onFlatViewChange={setFlatView}
            />
            <Column sx={OUTER_SX(SPACING)}>
                <TableContainer sx={{ marginTop: "0px !important" }}>
                    <MuiTable sx={TABLE_BODY_SX}>
                        {headerNode}
                        {bodyNode}
                    </MuiTable>
                </TableContainer>
                {table.totalCount > rowsPerPage && (
                    <Pagination
                        count={table.totalCount}
                        rowsPerPage={rowsPerPage}
                        page={table.page.page}
                        setPage={(newPage) => table.page.setPage(Math.min(newPage, table.page.maxPage))}
                    />
                )}
            </Column>
        </Column>
    );
}

const Table = genericsMemo(TableImpl);
export default Table;
export { Table };
